import DOMPurify from "dompurify";
import { useMemo } from "react";

const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Heuristic: does the body look like TipTap HTML output?  We treat presence
// of a block-level opening tag as the signal — this avoids false positives
// on the legacy markdown format that occasionally contains `<strong>`
// inline tags but never a `<p>` / `<h2>` / `<ul>` / `<blockquote>` etc.
const looksLikeHtml = (text) => /^\s*<(p|h[1-6]|ul|ol|blockquote|pre|table|figure|div|img|iframe)\b/i.test(text || "");

// Legacy markdown-ish renderer — preserved verbatim from the original
// BlogPost.jsx renderer so all pre-existing CMS blogs render identically.
const renderLegacyMarkdown = (text) => {
  const blocks = (text || "").split(/\n\n+/);
  return blocks.map((block, i) => {
    if (block.startsWith("## ")) {
      const label = block.replace(/^##\s+/, "").trim();
      return (
        <h2
          key={i}
          id={slugify(label)}
          className="font-serif text-3xl sm:text-4xl mt-14 mb-4 scroll-mt-24 text-[hsl(var(--foreground))]"
        >
          {label}
        </h2>
      );
    }
    return (
      <p
        key={i}
        className="prose-elegant mt-5 whitespace-pre-line"
        dangerouslySetInnerHTML={{ __html: block.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }}
      />
    );
  });
};

/**
 * RichContent — safe renderer for CMS-managed blog bodies.
 *
 * Auto-detects whether the body was authored with the new TipTap WYSIWYG
 * (raw HTML) or the legacy markdown-ish format (## headings + \n\n
 * paragraphs + **bold**).  This lets existing CMS blogs continue rendering
 * without a data migration — only newly-edited posts start using HTML.
 *
 * HTML is sanitized with DOMPurify to strip <script>, on* handlers, javascript:
 * URLs, etc.  We ALLOW the YouTube iframe from tiptap-extension-youtube by
 * enabling `ADD_TAGS: ["iframe"]` and validating the src via a hook so only
 * YouTube embed URLs are allowed.
 *
 * Props:
 *   text        string   Blog body (HTML or markdown-ish).
 *   className   string   Optional wrapper class overrides.
 */
const RichContent = ({ text, className = "" }) => {
  const isHtml = useMemo(() => looksLikeHtml(text), [text]);

  const cleanHtml = useMemo(() => {
    if (!isHtml || !text) return "";
    // Configure DOMPurify to allow YouTube embed iframes but nothing else.
    const config = {
      ADD_TAGS: ["iframe"],
      ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "target", "referrerpolicy"],
      // ALLOWED_URI_REGEXP is inherited from DOMPurify defaults which cover
      // http/https/mailto/tel; we further filter iframe src via a hook.
    };
    // Reset any prior hook so multiple RichContent mounts don't stack them.
    DOMPurify.removeAllHooks();
    DOMPurify.addHook("uponSanitizeElement", (node, data) => {
      if (data.tagName === "iframe") {
        const src = node.getAttribute("src") || "";
        // Only allow YouTube embed / nocookie embed URLs.
        const ok = /^https:\/\/(www\.)?(youtube(-nocookie)?\.com|youtu\.be)\//i.test(src);
        if (!ok) node.parentNode?.removeChild(node);
      }
      // Auto-assign id="…" to h2 / h3 elements so the sticky Table of
      // Contents can anchor-link to them.  Kept slugify-compatible with
      // the legacy markdown renderer's ids.
      if (["h2", "h3"].includes(data.tagName) && !node.getAttribute("id")) {
        const label = (node.textContent || "").trim();
        if (label) node.setAttribute("id", slugify(label));
      }
    });
    return DOMPurify.sanitize(text, config);
  }, [text, isHtml]);

  if (isHtml) {
    return (
      <div
        className={`rich-content prose-elegant ${className}`}
        data-testid="rich-content-html"
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
    );
  }
  return (
    <div className={className} data-testid="rich-content-markdown">
      {renderLegacyMarkdown(text)}
    </div>
  );
};

export default RichContent;
